import React, { useContext, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useSWR from 'swr';
import ProgressComponent from './progress-component';
import RewardsList from './RewardsList';
import PromotionsList from './PromotionsList';
import EarningsList from './EarningsList';
import './dashboard.css';
import { fetchUserPointsSWR, getUserPointsUrl } from './actions';
import ActivityPanel from './ActivityPanel';
import MemberCardModal from './MemberCardModal';
import { LOYALTY_DEFAULTS } from './constants';
import { ReactComponent as MemberCardQrIcon } from '../../assets/images/loyalty-member-card-qr.svg';
import { ReactComponent as CaretDownIcon } from '../../assets/images/loyalty-caret.svg';
import { ReactComponent as CaretUpIcon } from '../../assets/images/loyalty-caret-up.svg';
import {
  CustomerCategoryTitleUI,
  OrderUI,
} from '@next-order/next-order-components';
import { MenuContext } from 'contexts/MenuProvider';
import { BREAKPOINT_VALUES } from 'helpers/enums';
import { isLightColor } from './utils';

// const POINTS_VAL = 173;

const LoyaltyDashboard = ({
  userDetails,
  userLoyaltyDetails,
  storeConfig,
  themeColors,
  pointsTerminology = LOYALTY_DEFAULTS.POINTS_TERMINOLOGY,
}) => {
  const dispatch = useDispatch();
  const { breakPoint } = useContext(MenuContext);
  // State to control the activity panel visibility
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  // State to control the member card modal visibility
  const [isMemberCardOpen, setIsMemberCardOpen] = useState(false);
  const [rewardsSortBy, setRewardsSortBy] = useState('points_asc'); // Add sort state

  // Capitalize points terminology for display
  const capitalizedPointsTerminology =
    pointsTerminology.charAt(0).toUpperCase() + pointsTerminology.slice(1);

  // Toggle activity panel visibility
  const toggleActivity = () => {
    setIsActivityOpen((prevState) => !prevState);
  };

  // Open the member card modal
  const openMemberCard = () => {
    setIsMemberCardOpen(true);
  };

  // Close the member card modal
  const closeMemberCard = () => {
    setIsMemberCardOpen(false);
  };

  // Get store settings from Redux
  const { storeLoyaltySettings } = useSelector((state) => state.loyaltyReducer);
  const { currentOrder } = useSelector((state) => state.homeReducer);

  // Get the first store setting or null
  const storeSettings = storeLoyaltySettings || null;

  // Default points when loading
  const placeholderPoints = 100;

  // Prepare data for SWR
  const userId = userDetails?._id;
  const storeId = storeConfig?.storeId;
  const restaurantId = storeConfig?.restaurantId;
  const level = storeSettings?.level || LOYALTY_DEFAULTS.LEVEL;

  // Get the URL for SWR to fetch user points
  const pointsKey = getUserPointsUrl(userId, storeId, restaurantId, level);

  // Use SWR to fetch user points
  const {
    data: userPoints,
    error: pointsError,
    isLoading: isLoadingPoints,
  } = useSWR(pointsKey, fetchUserPointsSWR, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    dedupingInterval: 10000,
  });

  // Only show error state if there's an error with points
  if (pointsError) {
    return (
      <div className='loyalty-error'>
        Error loading {pointsTerminology}: {pointsError.message}
      </div>
    );
  }

  return (
    <div
      className={`loyalty-dashboard ${
        breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'pt-[50px]' : 'pt-4'
      }`}
    >
      <div className='loyalty-header'>
        <CustomerCategoryTitleUI>Loyalty</CustomerCategoryTitleUI>
        <div
          className='loyalty-header-membership'
          style={{
            color: isLightColor(themeColors.backgroundColor)
              ? themeColors.mainTextColor
              : themeColors.backgroundColor,
          }}
          onClick={openMemberCard}
        >
          <div className='qr-code-wrapper'>
            <MemberCardQrIcon />
          </div>
          <span>Member Card</span>
        </div>
      </div>

      <div className='loyalty-points-section'>
        <div className='loyalty-points-header'>
          <div className='points-header-container'>
            <h3>{capitalizedPointsTerminology} Balance</h3>
            <button
              className='activity-button'
              onClick={toggleActivity}
              style={{ color: 'rgb(31, 102, 229)' }}
            >
              <span className='activity-text'>Activity</span>
              {isActivityOpen ? (
                <CaretUpIcon className='h-4 w-4' />
              ) : (
                <CaretDownIcon className='h-4 w-4' />
              )}
            </button>
          </div>
          {isLoadingPoints ? (
            <span className='loyalty-points-value loading'>
              <span className='points-placeholder'></span>
            </span>
          ) : (
            <span className='loyalty-points-value'>
              {userPoints?.toLocaleString() || '0'}
            </span>
            // <span className="loyalty-points-value">{POINTS_VAL}</span>
          )}
        </div>

        <div className='loyalty-progress-container'>
          <ProgressComponent
            isLoadingPoints={isLoadingPoints}
            currentPoints={
              isLoadingPoints ? placeholderPoints : userPoints || 0
            }
            // currentPoints={POINTS_VAL}
            fillColor={themeColors.backgroundColor}
            labelTextColor={themeColors.mainTextColor}
            height='16px'
            labelFontSize='14px'
          />
        </div>

        <div className='activity-panel-container'>
          <ActivityPanel
            isOpen={isActivityOpen}
            onClose={() => setIsActivityOpen(false)}
            currentPoints={userPoints || 0}
            themeColors={themeColors}
            userDetails={userDetails}
            storeConfig={storeConfig}
            pointsTerminology={pointsTerminology}
          />
        </div>
      </div>

      <div className='loyalty-rewards-section'>
        <div className='rewards-header-container'>
          <h3 className='rewards-section-title'>My Rewards</h3>
          <select
            value={rewardsSortBy}
            onChange={(e) => {
              // Handle sort change
              setRewardsSortBy(e.target.value);
              console.log('Sort changed:', e.target.value);
            }}
            className='rewards-sort-dropdown'
          >
            <option value='points_asc'>
              By {capitalizedPointsTerminology}
            </option>
            <option value='name_asc'>By A-Z</option>
          </select>
        </div>
        <div className='rewards-section-content'>
          {userDetails && storeConfig && (
            <RewardsList
              userId={userDetails._id}
              storeId={storeConfig.storeId}
              restaurantId={storeConfig.restaurantId}
              level={level}
              themeColors={themeColors}
              userPoints={userPoints || 0}
              pointsTerminology={pointsTerminology}
              sortBy={rewardsSortBy}
              orderType={currentOrder?.orderType}
              orderFrom={currentOrder?.orderFrom}
              timeZone={storeConfig?.timeZone}
            />
          )}
        </div>
      </div>

      <div className='loyalty-bonus-section'>
        <OrderUI.Group title='Bonus Rewards'>
          <div className='bonus-section-content'>
            {userDetails && storeConfig && storeLoyaltySettings && (
              <PromotionsList
                settingId={storeLoyaltySettings.id}
                level={level}
                themeColors={themeColors}
                pointsTerminology={pointsTerminology}
              />
            )}
          </div>
        </OrderUI.Group>
      </div>

      <div className='loyalty-earn-section'>
        <OrderUI.Group title='Earn Rewards'>
          <div className='earn-section-content'>
            {userDetails && storeConfig && storeLoyaltySettings && (
              <EarningsList
                settingId={storeLoyaltySettings.id}
                level={level}
                themeColors={themeColors}
                pointsTerminology={pointsTerminology}
                userLoyaltyDetails={userLoyaltyDetails}
                storeConfig={storeConfig}
              />
            )}
          </div>
        </OrderUI.Group>
      </div>

      {/* Member Card Modal */}
      <MemberCardModal
        isOpen={isMemberCardOpen}
        onClose={closeMemberCard}
        userLoyaltyDetails={userLoyaltyDetails}
        userPoints={userPoints || 0}
        themeColors={themeColors}
        storeSettings={storeSettings}
        userDetails={userDetails}
        storeConfig={storeConfig}
      />
    </div>
  );
};

export default LoyaltyDashboard;
